import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from "react"
import clsx from "clsx"

export type ToggleGroupDirection = "horizontal" | "vertical";

export type ToggleGroupContextType<T extends any = any> = {
  selected: T | undefined
  setSelected: (selected: T | undefined) => void
}

export const ToggleGroupContext = createContext<ToggleGroupContextType | undefined>(undefined)

export const useToggleGroup = <T extends any = any>() => {
  return useContext(ToggleGroupContext as React.Context<ToggleGroupContextType<T>>)
}

export type ToggleGroupProviderProps<T extends any = any> = {
  children?: ReactNode
  selected?: T
  enableToggle?: boolean
  onChange?: (selected: T | undefined) => void
}

export const ToggleGroupProvider:FC<ToggleGroupProviderProps> = (props) => {
  const [selected, setSelected] = useState<string | undefined>(props.selected || null)

  const context: ToggleGroupContextType = useMemo(() => ({
    selected,
    setSelected: (value: string | undefined) => {
      if (props.enableToggle) {
        setSelected(value === selected ? undefined : value)
        props.onChange?.(value === selected ? undefined : value)
      } else {
        setSelected(value)
        props.onChange?.(value)
      }
    }
  }), [selected, setSelected, props.enableToggle])

  useEffect(() => {
    setSelected(props.selected || undefined)
  }, [props.selected])

  return (
    <ToggleGroupContext.Provider value={context}>
      {props.children}
    </ToggleGroupContext.Provider>
  )
}

export type ToggleGroupProps<T extends any = any> = {
  className?: string
  children: ReactNode
  selected?: string
  enableToggle?: boolean
  onChange?: (selected: T | undefined) => void
  /** Layout direction. Affects button border-radius styling. */
  direction?: ToggleGroupDirection
}

/**
 * ToggleGroup wraps Button components to create a single-select button group.
 * Buttons inside automatically detect they're in a ToggleGroup via context.
 * 
 * @example
 * <ToggleGroup selected={view} onChange={setView} direction="horizontal">
 *   <Button value="grid" icon={<GridIcon />} intent="secondary" selectedIntent="primary" />
 *   <Button value="list" icon={<ListIcon />} intent="secondary" selectedIntent="primary" />
 * </ToggleGroup>
 */
export const ToggleGroup = <T extends any = any>(props: ToggleGroupProps<T>) => {
  const { direction = "horizontal" } = props;
  
  // Border-radius styling for connected buttons based on direction
  // Buttons may be wrapped in ToolTip divs (inline-block), so we target:
  // - Buttons inside wrapper divs: [&>:first-child_button]
  // - Direct button children: [&>button:first-child]
  const directionClasses = direction === "horizontal" 
    ? [
        // First: keep left rounding, remove right
        "[&>:first-child_button]:!rounded-r-none",
        "[&>button:first-child]:!rounded-r-none",
        // Last: keep right rounding, remove left  
        "[&>:last-child_button]:!rounded-l-none",
        "[&>button:last-child]:!rounded-l-none",
        // Middle: remove all rounding
        "[&>:not(:first-child):not(:last-child)_button]:!rounded-none",
        "[&>button:not(:first-child):not(:last-child)]:!rounded-none",
      ]
    : [
        // First: keep top rounding, remove bottom
        "[&>:first-child_button]:!rounded-b-none",
        "[&>button:first-child]:!rounded-b-none",
        // Last: keep bottom rounding, remove top
        "[&>:last-child_button]:!rounded-t-none",
        "[&>button:last-child]:!rounded-t-none",
        // Middle: remove all rounding
        "[&>:not(:first-child):not(:last-child)_button]:!rounded-none",
        "[&>button:not(:first-child):not(:last-child)]:!rounded-none",
      ];

  const wrapperClasses = clsx(
    "flex",
    direction === "horizontal" ? "flex-row" : "flex-col",
    directionClasses,
    props.className
  );

  return (
    <ToggleGroupProvider
      selected={props.selected}
      enableToggle={props.enableToggle}
      onChange={props.onChange}
    >
      <div className={wrapperClasses}>
        {props.children}
      </div>
    </ToggleGroupProvider>
  );
}